// Tickets SAV - Espace Client
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket, Search, Plus, Clock, CheckCircle2, AlertCircle, XCircle,
  MessageSquare, ChevronRight, Filter, Calendar, User, Package
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Priorités
const PRIORITIES = {
  low: { color: 'bg-gray-100 text-gray-700', label: 'Basse' },
  medium: { color: 'bg-yellow-100 text-yellow-700', label: 'Moyenne' },
  high: { color: 'bg-orange-100 text-orange-700', label: 'Haute' },
  urgent: { color: 'bg-red-100 text-red-700', label: 'Urgente' },
};

// Statuts
const STATUSES = {
  open: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Ouvert', icon: AlertCircle },
  in_progress: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'En cours', icon: Clock },
  waiting: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'En attente client', icon: Clock },
  resolved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Résolu', icon: CheckCircle2 },
  closed: { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Fermé', icon: XCircle },
};

// Catégories
const CATEGORIES = [
  { id: 'product', label: 'Produit défectueux' },
  { id: 'delivery', label: 'Problème de livraison' },
  { id: 'refund', label: 'Remboursement' },
  { id: 'order', label: 'Erreur de commande' },
  { id: 'other', label: 'Autre' },
];

// Carte ticket
function TicketCard({ ticket, onClick }) {
  const status = STATUSES[ticket.status] || STATUSES.open;
  const priority = PRIORITIES[ticket.priority] || PRIORITIES.medium;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onClick={() => onClick(ticket)}
      className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono text-gray-500">#{ticket.id}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}>
            {priority.label}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </span>
      </div>

      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">{ticket.subject}</h3>
      <p className="text-sm text-gray-500 line-clamp-2 mb-3">{ticket.description}</p>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <User size={12} /> {ticket.customer}
          </span>
          {ticket.orderId && (
            <span className="flex items-center gap-1">
              <Package size={12} /> {ticket.orderId}
            </span>
          )}
        </div>
        <span className="flex items-center gap-1">
          <Calendar size={12} /> {ticket.date}
        </span>
      </div>

      {ticket.messages > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
          <MessageSquare size={14} />
          <span>{ticket.messages} message(s)</span>
        </div>
      )}
    </motion.div>
  );
}

// Modal détail ticket
function TicketDetailModal({ ticket, isOpen, onClose, onUpdateStatus }) {
  const [reply, setReply] = useState('');

  if (!isOpen || !ticket) return null;

  const status = STATUSES[ticket.status] || STATUSES.open;
  const priority = PRIORITIES[ticket.priority] || PRIORITIES.medium;
  const StatusIcon = status.icon;

  const handleSendReply = () => {
    if (!reply.trim()) return;
    toast.success('Réponse envoyée');
    setReply('');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-sm font-mono text-gray-500">Ticket #{ticket.id}</span>
                <h2 className="text-xl font-bold text-gray-900 mt-1">{ticket.subject}</h2>
              </div>
              <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border ${status.color}`}>
                <StatusIcon size={14} />
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className={`px-2 py-0.5 rounded-full ${priority.color}`}>{priority.label}</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-500">{ticket.customer}</span>
              {ticket.orderId && (
                <>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-500">Commande {ticket.orderId}</span>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Description */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700">{ticket.description}</p>
              </div>
            </div>

            {/* Timeline / Messages */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Historique</h3>
              
              {ticket.history?.map((item, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare size={14} className="text-gray-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{item.author}</span>
                      <span className="text-xs text-gray-400">{item.date}</span>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-3">
                      <p className="text-sm text-gray-700">{item.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply */}
          <div className="p-6 border-t border-gray-100 bg-gray-50">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Écrire une réponse..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-2">
                <select
                  onChange={(e) => onUpdateStatus(ticket, e.target.value)}
                  value={ticket.status}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="open">Ouvert</option>
                  <option value="in_progress">En cours</option>
                  <option value="waiting">En attente client</option>
                  <option value="resolved">Résolu</option>
                  <option value="closed">Fermé</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="rounded-xl">
                  Fermer
                </Button>
                <Button 
                  onClick={handleSendReply}
                  disabled={!reply.trim()}
                  className="bg-orange-500 hover:bg-orange-600 rounded-xl"
                >
                  Envoyer
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Modal nouveau ticket
function NewTicketModal({ isOpen, onClose, onCreate }) {
  const [form, setForm] = useState({
    subject: '',
    category: 'product',
    priority: 'medium',
    orderId: '',
    description: ''
  });

  const handleSubmit = () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    onCreate(form);
    setForm({ subject: '', category: 'product', priority: 'medium', orderId: '', description: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6">Nouveau ticket</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Décrivez brièvement le problème"
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl"
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">N° Commande (optionnel)</label>
            <Input
              value={form.orderId}
              onChange={(e) => setForm({ ...form, orderId: e.target.value })}
              placeholder="Ex: CMD-2024-001"
              className="rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Décrivez le problème en détail..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
            Annuler
          </Button>
          <Button onClick={handleSubmit} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
            Créer le ticket
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      // Données simulées
      setTickets([
        {
          id: 'TKT-001',
          subject: 'Produit reçu endommagé',
          description: 'La perceuse reçue présente des traces de choc sur le boîtier. Le mandrin semble également faussé.',
          category: 'product',
          priority: 'high',
          status: 'open',
          customer: 'Marie Lambert',
          orderId: 'CMD-2024-001',
          date: '15 Jan 2025',
          messages: 2,
          history: [
            { author: 'Marie Lambert', date: '15 Jan, 14:30', message: 'La perceuse reçue présente des traces de choc sur le boîtier.' },
            { author: 'Support', date: '15 Jan, 15:00', message: 'Bonjour, nous sommes désolés pour ce désagrément. Pouvez-vous nous envoyer des photos ?' },
          ]
        },
        {
          id: 'TKT-002',
          subject: 'Colis non reçu',
          description: 'Ma commande devait arriver il y a 5 jours mais je n\'ai toujours rien reçu.',
          category: 'delivery',
          priority: 'urgent',
          status: 'in_progress',
          customer: 'Pierre Durand',
          orderId: 'CMD-2024-002',
          date: '14 Jan 2025',
          messages: 4,
          history: [
            { author: 'Pierre Durand', date: '14 Jan, 09:00', message: 'Ma commande devait arriver il y a 5 jours.' },
            { author: 'Support', date: '14 Jan, 10:00', message: 'Nous vérifions avec le transporteur.' },
          ]
        },
        {
          id: 'TKT-003',
          subject: 'Demande de remboursement',
          description: 'Je souhaite être remboursé pour ma dernière commande car le produit ne correspond pas à la description.',
          category: 'refund',
          priority: 'medium',
          status: 'waiting',
          customer: 'Sophie Martin',
          date: '13 Jan 2025',
          messages: 1,
          history: []
        },
        {
          id: 'TKT-004',
          subject: 'Question sur garantie',
          description: 'Comment fonctionne la garantie sur les outils électroportatifs ?',
          category: 'other',
          priority: 'low',
          status: 'resolved',
          customer: 'Jean Petit',
          date: '10 Jan 2025',
          messages: 3,
          history: []
        },
      ]);
    } catch (e) {
      console.error('Error fetching tickets:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = (form) => {
    const newTicket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
      ...form,
      status: 'open',
      customer: 'Toi',
      date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
      messages: 0,
      history: []
    };
    setTickets([newTicket, ...tickets]);
    toast.success('Ticket créé avec succès');
  };

  const handleUpdateStatus = (ticket, newStatus) => {
    setTickets(tickets.map(t => 
      t.id === ticket.id ? { ...t, status: newStatus } : t
    ));
    setSelectedTicket({ ...ticket, status: newStatus });
    toast.success('Statut mis à jour');
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          t.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    waiting: tickets.filter(t => t.status === 'waiting').length,
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Tickets SAV</h1>
          <p className="text-gray-500">Gérez les demandes de support client</p>
        </div>
        <Button
          onClick={() => setShowNewTicketModal(true)}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Nouveau ticket
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle size={24} className="text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats.open}</p>
              <p className="text-sm text-blue-600">Ouverts</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">{stats.inProgress}</p>
              <p className="text-sm text-yellow-600">En cours</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <User size={24} className="text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-700">{stats.waiting}</p>
              <p className="text-sm text-purple-600">En attente</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher un ticket..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
        >
          <option value="all">Tous les statuts</option>
          <option value="open">Ouverts</option>
          <option value="in_progress">En cours</option>
          <option value="waiting">En attente</option>
          <option value="resolved">Résolus</option>
          <option value="closed">Fermés</option>
        </select>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Ticket size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucun ticket</h3>
          <p className="text-gray-500">Les tickets de support apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredTickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onClick={setSelectedTicket}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onUpdateStatus={handleUpdateStatus}
      />

      <NewTicketModal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onCreate={handleCreateTicket}
      />
    </div>
  );
}
